/**
 * The six synthetic enemy archetypes from deep-research-report.md
 * §"시뮬레이션 보정". These are bootstrap proposals, NOT a specific game's stats:
 * the point is heterogeneity — you cannot price anti-heal without a Healer, AoE
 * without a Swarm, or burst/CC without an Elite/Boss.
 */

import type { DamageComponent, DamageSourceKind } from './combat.js';
import type { CreateStatusInput } from './state.js';

/** The product-facing axes of a monster are intentionally independent. */
export type MonsterGrade = 'weak' | 'standard' | 'elite' | 'boss';
export type MonsterAggroPolicy = 'PROACTIVE' | 'REACTIVE' | 'proactive' | 'reactive';
export type MonsterSociality = 'SOLITARY' | 'HERD' | 'PACK' | 'solitary' | 'herd' | 'pack';
export type MonsterDeliveryKind = 'MELEE' | 'PROJECTILE' | 'CHARGE' | 'HYBRID' | 'melee' | 'projectile' | 'charge' | 'hybrid';

export interface MonsterProjectileAuthoring {
  /** Maximum flight/attack range. Omitted legacy projectile authoring falls
   * back to the attack's range; hybrid presets author this independently from
   * their close-contact melee range. */
  readonly rangeM?: number;
  readonly speedMps: number;
  readonly radiusM: number;
  readonly lifetimeS: number;
  /** A ranged monster retreats only while the target is inside this band. */
  readonly retreatRangeM?: number;
  readonly retreatSpeedMps?: number;
}

export interface MonsterChargeAuthoring {
  readonly windupS: number;
  readonly speedMps: number;
  readonly maxDistanceM: number;
  readonly impactRadiusM: number;
  readonly recoveryS: number;
  /** Optional charge-only recast cooldown. When absent, attack cadence is
   * retained for compatibility with the first explicit charge authoring. */
  readonly cooldownS?: number;
  /** Optional distance band in which a charge may start. The normal melee
   * attack remains available below the lower bound. */
  readonly triggerRangeMinM?: number;
  readonly triggerRangeMaxM?: number;
  /** Optional impact override; otherwise the attack's normal components apply. */
  readonly damageComponents?: readonly DamageComponent[];
  readonly statusEffects?: readonly CreateStatusInput[];
}

export interface MonsterAttackDeliveryAuthoring {
  readonly type: MonsterDeliveryKind;
  readonly projectile?: MonsterProjectileAuthoring;
  readonly charge?: MonsterChargeAuthoring;
}

/**
 * Per-spawn behavior. It is opt-in: an omitted behavior keeps the historical
 * always-proactive, no-leash melee fixture exactly as it was. Grade is
 * descriptive metadata and never changes hp or damage.
 */
export interface MonsterBehavior {
  readonly version: 'monster-behavior-v1';
  readonly grade?: MonsterGrade;
  readonly aggro?: MonsterAggroPolicy;
  /** Friendly spelling for scenario authors; `aggro` wins if both are set. */
  readonly disposition?: 'PASSIVE' | 'AGGRESSIVE' | 'passive' | 'aggressive';
  readonly sociality?: MonsterSociality;
  readonly packId?: string;
  readonly supportRadiusM?: number;
  readonly aggroRangeM?: number;
  readonly leashRangeM?: number;
  readonly homeRadiusM?: number;
  /** Opt-in idle roaming around the spawn home, interrupted by engagement. */
  readonly wander?: { readonly radiusM: number; readonly speedRatio: number; readonly moveS: number; readonly pauseS: number };
  readonly delivery?: MonsterAttackDeliveryAuthoring;
}

export interface MonsterAttackAuthoring {
  readonly rangeM: number;
  readonly intervalS: number;
  readonly damageComponents?: readonly DamageComponent[];
  readonly sourceKind?: Extract<DamageSourceKind, 'DIRECT' | 'PERIODIC'>;
  readonly statusEffects?: readonly CreateStatusInput[];
  readonly delivery?: MonsterAttackDeliveryAuthoring;
}
export interface MonsterAttackSpec {
  readonly rangeM: number;
  readonly intervalTicks: number;
  readonly damageComponents: readonly DamageComponent[];
  readonly sourceKind: 'DIRECT' | 'PERIODIC';
  readonly statusEffects?: readonly CreateStatusInput[];
  readonly delivery?: MonsterAttackDeliveryAuthoring;
}
export function resolveMonsterAttack(authoring: MonsterAttackAuthoring, threatPerS: number, tickRate: number): MonsterAttackSpec {
  if (!(Number.isFinite(tickRate) && tickRate > 0) || !(Number.isFinite(threatPerS) && threatPerS >= 0) || !(Number.isFinite(authoring.rangeM) && authoring.rangeM > 0) || !(Number.isFinite(authoring.intervalS) && authoring.intervalS > 0)) throw new Error('invalid monster attack authoring');
  const intervalTicks = Math.max(1, Math.ceil(authoring.intervalS * tickRate));
  if (!Number.isSafeInteger(intervalTicks)) throw new Error('invalid monster attack interval');
  const components = authoring.damageComponents === undefined
    ? [Object.freeze({ amount: threatPerS * intervalTicks / tickRate, damageClass: 'PHYSICAL' as const, attribute: 'NONE' as const, mitigationMode: 'NORMAL' as const })]
    : authoring.damageComponents.map((component) => {
      if (!(Number.isFinite(component.amount) && component.amount > 0)) throw new Error('invalid monster attack component');
      return Object.freeze({ ...component });
    });
  if (components.some((component) => !(Number.isFinite(component.amount) && component.amount > 0))) throw new Error('invalid bootstrap monster attack component');
  if ((authoring.sourceKind ?? 'DIRECT') === 'DIRECT') components.forEach(refuseDirectToxic);
  if (authoring.delivery) validateMonsterDelivery(authoring.delivery);
  const statusEffects = Object.freeze((authoring.statusEffects ?? []).map((effect) => Object.freeze({ ...effect })));
  return Object.freeze({ rangeM: authoring.rangeM, intervalTicks, damageComponents: Object.freeze(components), sourceKind: authoring.sourceKind ?? 'DIRECT',
    ...(authoring.statusEffects === undefined ? {} : { statusEffects }), ...(authoring.delivery === undefined ? {} : { delivery: structuredClone(authoring.delivery) }) });
}

/** Toxic damage reaches a target only as poison, a status effect: a monster's hit is never toxic. */
function refuseDirectToxic(component: DamageComponent): void {
  if (component.attribute === 'TOXIC') throw new Error('invalid monster damage component: toxic damage reaches a target only as poison (a status effect), never as a direct hit');
}

function validateMonsterDelivery(delivery: MonsterAttackDeliveryAuthoring): void {
  if (!delivery || typeof delivery !== 'object' || Array.isArray(delivery) || typeof delivery.type !== 'string') throw new Error('invalid monster delivery authoring');
  const kind = delivery.type.toUpperCase();
  if (kind !== 'MELEE' && kind !== 'PROJECTILE' && kind !== 'CHARGE' && kind !== 'HYBRID') throw new Error('invalid monster delivery type');
  if (kind === 'PROJECTILE' || kind === 'HYBRID') {
    const projectile = delivery.projectile;
    if (!projectile || ![projectile.speedMps, projectile.radiusM, projectile.lifetimeS].every((value) => Number.isFinite(value) && value > 0)) throw new Error('invalid monster projectile authoring');
    if (projectile.rangeM !== undefined && !(Number.isFinite(projectile.rangeM) && projectile.rangeM > 0)) throw new Error('invalid monster projectile range');
    if (projectile.retreatRangeM !== undefined && !(Number.isFinite(projectile.retreatRangeM) && projectile.retreatRangeM > 0)) throw new Error('invalid monster retreat range');
    if (projectile.retreatSpeedMps !== undefined && !(Number.isFinite(projectile.retreatSpeedMps) && projectile.retreatSpeedMps > 0)) throw new Error('invalid monster retreat speed');
  }
  if (kind === 'CHARGE') {
    const charge = delivery.charge;
    if (!charge || ![charge.windupS, charge.speedMps, charge.maxDistanceM, charge.impactRadiusM, charge.recoveryS].every((value) => Number.isFinite(value) && value > 0)) throw new Error('invalid monster charge authoring');
    for (const value of [charge.cooldownS, charge.triggerRangeMinM, charge.triggerRangeMaxM]) if (value !== undefined && !(Number.isFinite(value) && value > 0)) throw new Error('invalid monster charge timing/range authoring');
    if (charge.triggerRangeMinM !== undefined && charge.triggerRangeMaxM !== undefined && charge.triggerRangeMinM > charge.triggerRangeMaxM) throw new Error('invalid monster charge trigger range');
    for (const component of charge.damageComponents ?? []) if (!(Number.isFinite(component.amount) && component.amount > 0)) throw new Error('invalid monster charge damage component');
    charge.damageComponents?.forEach(refuseDirectToxic);
  }
}

/** Runtime boundary validation for direct AuthoritativeWorld callers. */
export function validateMonsterBehavior(behavior: MonsterBehavior | undefined): void {
  if (behavior === undefined) return;
  if (!behavior || typeof behavior !== 'object' || Array.isArray(behavior)) throw new Error('invalid monster behavior');
  if (behavior.version !== 'monster-behavior-v1') throw new Error('invalid monster behavior version');
  if (behavior.aggro !== undefined && (typeof behavior.aggro !== 'string' || !['PROACTIVE', 'REACTIVE', 'proactive', 'reactive'].includes(behavior.aggro))) throw new Error('invalid monster aggro policy');
  if (behavior.disposition !== undefined && (typeof behavior.disposition !== 'string' || !['PASSIVE', 'AGGRESSIVE', 'passive', 'aggressive'].includes(behavior.disposition))) throw new Error('invalid monster disposition');
  if (behavior.sociality !== undefined && (typeof behavior.sociality !== 'string' || !['SOLITARY', 'HERD', 'PACK', 'solitary', 'herd', 'pack'].includes(behavior.sociality))) throw new Error('invalid monster sociality');
  for (const value of [behavior.supportRadiusM, behavior.aggroRangeM, behavior.leashRangeM, behavior.homeRadiusM]) if (value !== undefined && !(Number.isFinite(value) && value > 0)) throw new Error('invalid monster behavior radius');
  if (behavior.packId !== undefined && (!behavior.packId || typeof behavior.packId !== 'string')) throw new Error('invalid monster pack id');
  if (typeof behavior.sociality === 'string' && (behavior.sociality.toUpperCase() === 'HERD' || behavior.sociality.toUpperCase() === 'PACK') && behavior.packId === undefined) throw new Error('social monster behavior requires pack id');
  if (behavior.wander) {
    const w = behavior.wander;
    if (![w.radiusM, w.speedRatio, w.moveS, w.pauseS].every(v => Number.isFinite(v) && v > 0) || w.speedRatio > 1) throw new Error('invalid monster wander');
  }
  if (behavior.delivery) validateMonsterDelivery(behavior.delivery);
}

/** Merge a declared partial attack override onto a base authoring, field by
 * field, so an override that only touches `rangeM` leaves the archetype's
 * cadence/components/sourceKind untouched. */
export function resolveMonsterAttackOverride(
  base: MonsterAttackAuthoring,
  override: Partial<MonsterAttackAuthoring> | undefined,
): MonsterAttackAuthoring {
  if (!override) return base;
  return {
    rangeM: override.rangeM ?? base.rangeM,
    intervalS: override.intervalS ?? base.intervalS,
    damageComponents: override.damageComponents ?? base.damageComponents,
    sourceKind: override.sourceKind ?? base.sourceKind,
    statusEffects: override.statusEffects ?? base.statusEffects,
    delivery: override.delivery ?? base.delivery,
  };
}

export interface Archetype {
  id: string;
  weight: number; // spawn share
  hp: number;
  /**
   * Bootstrap threat budget expressed as raw damage-equivalent per second.
   * It is used to derive initial attack magnitude and for encounter budgeting.
   * It is not observed post-mitigation DPS.
   */
  threatPerS: number;
  moveMult: number; // multiplier on base enemy move speed
  healPerS: number; // ally healing throughput (support only)
  ccSusceptibility: number; // χ in [0,1]
  displacementResistance: number; // 0 movable, 1 immovable
  densityPerM2: number; // spatial intensity ρ for spawn clustering
  /** Optional circular combat-contact radius. Omitted legacy archetypes retain
   * the historical 0.5m radius exactly. ENC.1 reference hitboxes use this
   * narrow seam so T.1 skill-contact geometry consumes the authored radius. */
  hitboxRadiusM?: number;
  attack: MonsterAttackAuthoring;
  /** Optional behavior preset. Omitted archetypes are legacy-compatible. */
  behavior?: MonsterBehavior;
}

export const DEFAULT_ENEMY_HITBOX_RADIUS_M = 0.5;

/** Resolve the circular target radius used by skill-contact geometry. Invalid
 * explicit values fail closed rather than silently falling back to legacy. */
export function resolveEnemyHitboxRadiusM(archetype: Pick<Archetype, 'hitboxRadiusM'>): number {
  const radius = archetype.hitboxRadiusM ?? DEFAULT_ENEMY_HITBOX_RADIUS_M;
  if (!(Number.isFinite(radius) && radius > 0)) throw new Error('invalid enemy hitbox radius');
  return radius;
}

export const ARCHETYPES: Record<string, Archetype> = {
  melee: {
    id: 'melee',
    weight: 0.3,
    hp: 100,
    threatPerS: 5,
    moveMult: 1.0,
    healPerS: 0,
    ccSusceptibility: 0.9,
    displacementResistance: 0,
    densityPerM2: 0.02,
    attack: { rangeM: 1.5, intervalS: 1 },
  },
  ranged: {
    id: 'ranged',
    weight: 0.2,
    hp: 80,
    threatPerS: 5, // PROVISIONAL: ranged hits like melee (was 6)
    moveMult: 1.1,
    healPerS: 0,
    ccSusceptibility: 1.0,
    displacementResistance: 0,
    densityPerM2: 0.015,
    attack: { rangeM: 8, intervalS: 1 },
  },
  healer: {
    id: 'healer',
    weight: 0.15,
    hp: 75,
    threatPerS: 3,
    moveMult: 1.0,
    healPerS: 3,
    ccSusceptibility: 0.9,
    displacementResistance: 0.1,
    densityPerM2: 0.012,
    attack: { rangeM: 1.5, intervalS: 1 },
  },
  swarm: {
    id: 'swarm',
    weight: 0.2,
    hp: 40,
    threatPerS: 2,
    moveMult: 1.0,
    healPerS: 0,
    ccSusceptibility: 1.0,
    displacementResistance: 0,
    densityPerM2: 0.07,
    attack: { rangeM: 1.5, intervalS: 1 },
  },
  elite: {
    id: 'elite',
    weight: 0.1,
    hp: 220,
    threatPerS: 10,
    moveMult: 1.0,
    healPerS: 0,
    ccSusceptibility: 0.5,
    displacementResistance: 0.5,
    densityPerM2: 0.01,
    attack: { rangeM: 1.5, intervalS: 1 },
  },
  boss: {
    id: 'boss',
    weight: 0.05,
    hp: 1000,
    threatPerS: 18,
    moveMult: 0.9,
    healPerS: 1,
    ccSusceptibility: 0.15,
    displacementResistance: 1,
    densityPerM2: 0.003,
    attack: { rangeM: 1.5, intervalS: 1 },
  },
};

export const ARCHETYPE_LIST: Archetype[] = Object.values(ARCHETYPES);

/**
 * Versioned behavior examples for Combat Lab authors. These are composition
 * data only: grade, aggro, sociality and delivery can be replaced separately,
 * and none of them alter an archetype's HP or attack budget.
 */
export const MONSTER_BEHAVIOR_PRESETS = Object.freeze({
  reactiveMeleeHerbivore: Object.freeze({ version: 'monster-behavior-v1' as const, grade: 'weak' as const, aggro: 'REACTIVE' as const, sociality: 'SOLITARY' as const, delivery: Object.freeze({ type: 'MELEE' as const }) }),
  reactiveHerdSupport: Object.freeze({ version: 'monster-behavior-v1' as const, grade: 'standard' as const, aggro: 'REACTIVE' as const, sociality: 'HERD' as const, packId: 'herd-example' as const, supportRadiusM: 8, delivery: Object.freeze({ type: 'MELEE' as const }) }),
  proactiveMelee: Object.freeze({ version: 'monster-behavior-v1' as const, grade: 'standard' as const, aggro: 'PROACTIVE' as const, sociality: 'SOLITARY' as const, delivery: Object.freeze({ type: 'MELEE' as const }) }),
  proactivePackCharge: Object.freeze({ version: 'monster-behavior-v1' as const, grade: 'elite' as const, aggro: 'PROACTIVE' as const, sociality: 'PACK' as const, packId: 'pack-example' as const, supportRadiusM: 10, leashRangeM: 20, delivery: Object.freeze({ type: 'CHARGE' as const, charge: Object.freeze({ windupS: 0.6, speedMps: 12, maxDistanceM: 10, impactRadiusM: 1.2, recoveryS: 0.4, cooldownS: 8, triggerRangeMinM: 4, triggerRangeMaxM: 12 }) }) }),
  proactiveRanged: Object.freeze({ version: 'monster-behavior-v1' as const, grade: 'standard' as const, aggro: 'PROACTIVE' as const, sociality: 'SOLITARY' as const, delivery: Object.freeze({ type: 'PROJECTILE' as const, projectile: Object.freeze({ rangeM: 18, speedMps: 10, radiusM: 0.2, lifetimeS: 2.5, retreatRangeM: 5, retreatSpeedMps: 2.4 }) }) }),
  rangedMeleePack: Object.freeze({ version: 'monster-behavior-v1' as const, grade: 'elite' as const, aggro: 'PROACTIVE' as const, sociality: 'PACK' as const, packId: 'mixed-pack-example' as const, supportRadiusM: 9, delivery: Object.freeze({ type: 'HYBRID' as const, projectile: Object.freeze({ rangeM: 18, speedMps: 10, radiusM: 0.2, lifetimeS: 2.5, retreatRangeM: 4 }) }) }),
});
/** Short alias used by experiment authors. */
export const MONSTER_PRESETS = MONSTER_BEHAVIOR_PRESETS;

/** Base enemy movement speed (m/s) that moveMult scales. Set just under the
 *  player's base speed so kiting is contested and movement buffs have real value. */
export const BASE_ENEMY_SPEED = 3.0;
